"use client"
import Accordion from "react-bootstrap/Accordion"
import { AiFillDashboard, AiOutlineDownload, AiOutlineThunderbolt, AiOutlineBulb, AiOutlineRobot, AiOutlineSafetyCertificate, AiOutlineBank, AiOutlineTeam, AiOutlineInbox, AiOutlineUser, AiOutlineBook, AiOutlineIdcard, AiOutlineLineChart } from "react-icons/ai"
import { useIsAdmin } from "../../hooks/useIsAdmin"
import { useSchoolRole } from "../../hooks/useSchoolRole"
import { BiSpreadsheet, BiBookOpen } from "react-icons/bi"
import { IoIosPeople } from "react-icons/io"
import { UserAuth } from "../../context/AuthContext"
import UserGuide from "../../../src/manual.pdf"
import UserGuideEn from "../../../src/manual-en.pdf"
import UserGuideTa from "../../../src/manual-ta.pdf"
import { useTranslations } from "../../translations"
import { Link, NavLink, useNavigate } from "react-router-dom"
import { useLanguage } from "../../context/LanguageContext"
import { useCallback } from "react"
import { RxExit } from "react-icons/rx"
import LanguageSelector from "../LanguageSelector"

// Monetización oculta por defecto: se activa con REACT_APP_MONETIZATION_ENABLED=true al compilar.
const MONETIZATION_ENABLED = process.env.REACT_APP_MONETIZATION_ENABLED === "true"

const Menu = ({ show, toggle }) => {
  const { user, logout } = UserAuth()
  const { t } = useTranslations()
  const { language } = useLanguage()
  const { isAdmin, loading: adminLoading } = useIsAdmin()
  const { role, loading: roleLoading } = useSchoolRole()
  const resolvingRole = adminLoading || roleLoading
  const navigate = useNavigate()

  // Mismo cierre de sesión que el header: el menú lateral es la única vía en móvil.
  const handleLogout = useCallback(async () => {
    try {
      await logout()
      navigate("/")
    } catch (e) {
      /* silencioso: igual que en el header */
    }
  }, [logout, navigate])

  const getManual = () => {
    if (language === "ta") return UserGuideTa
    if (language === "en") return UserGuideEn
    return UserGuide
  }

  const getManualName = () => {
    if (language === "ta") return "MiABC-பயனர்-கையேடு"
    if (language === "en") return "MiABC-User-Manual"
    return "Manual-de-Usuario-MiABC"
  }

  return (
    <>
    <nav id="sidebarMenu" className={`sidebar-modern ${show === "show" ? "show" : ""}`}>
      <div className="sidebar-brand d-none d-md-block text-center mt-2">
        <img 
          src="/assets/img/landing-images/MiABC-horizontal.png" 
          alt="Logo" 
          style={{ filter: 'brightness(0) invert(1)', height: "45px", width: "auto" }} 
        />
      </div>
      
      <div className="position-sticky">
        <ul className="nav flex-column">
          {resolvingRole ? null : isAdmin ? (
            <>
              <li className="nav-item">
                <Link className="nav-link" to="/admin/payments">
                  <AiOutlineSafetyCertificate size={20} />
                  {t("adminPaymentsTitle")}
                </Link>
              </li>
              <li className="nav-item">
                <Link className="nav-link" to="/admin/schools">
                  <AiOutlineBank size={20} />
                  {t("adminSchoolsTitle")}
                </Link>
              </li>
              <li className="nav-item">
                <Link className="nav-link" to="/admin/leads">
                  <AiOutlineInbox size={20} />
                  {t("adminLeadsTitle")}
                </Link>
              </li>
              <li className="nav-item">
                <Link className="nav-link" to="/admin/events">
                  <AiOutlineLineChart size={20} />
                  {t("adminEventsTitle")}
                </Link>
              </li>
            </>
          ) : role === "escuela" ? (
            <>
              <li className="nav-item">
                <NavLink className="nav-link" to="/school/teachers">
                  <AiOutlineUser size={20} />
                  {t("teachers")}
                </NavLink>
              </li>
              <li className="nav-item">
                <NavLink className="nav-link" to="/school/grades">
                  <AiOutlineBook size={20} />
                  {t("grades")}
                </NavLink>
              </li>
              <li className="nav-item">
                <NavLink className="nav-link" to="/school/sections">
                  <AiOutlineTeam size={20} />
                  {t("sections")}
                </NavLink>
              </li>
              <li className="nav-item">
                <NavLink className="nav-link" to="/school/students">
                  <AiOutlineIdcard size={20} />
                  {t("students")}
                </NavLink>
              </li>
              <li className="nav-item">
                <NavLink className="nav-link" to="/school/representatives">
                  <IoIosPeople size={20} />
                  {t("representatives")}
                </NavLink>
              </li>
              <li className="nav-item">
                <NavLink className="nav-link" to="/activity">
                  <AiOutlineLineChart size={20} />
                  {t("clsTitle")}
                </NavLink>
              </li>
            </>
          ) : role === "maestro" ? (
            <>
              <li className="nav-item">
                <NavLink className="nav-link" to="/teacher">
                  <AiOutlineTeam size={20} />
                  {t("teacherPanelTitle")}
                </NavLink>
              </li>
              <li className="nav-item">
                <NavLink className="nav-link" to="/activity">
                  <AiOutlineLineChart size={20} />
                  {t("clsTitle")}
                </NavLink>
              </li>
              <Accordion className="nav-accordion">
                <Accordion.Item eventKey="0">
                  <Accordion.Header>
                    <AiOutlineThunderbolt size={20} />
                    {t("actions")}
                  </Accordion.Header>
                  <Accordion.Body>
                    <li className="nav-item">
                      <NavLink className="nav-link" to="/actions/create-text">{t("createText")}</NavLink>
                    </li>
                    <li className="nav-item">
                      <NavLink className="nav-link" to="/actions/list-texts">{t("textList")}</NavLink>
                    </li>
                    <li className="nav-item">
                      <NavLink className="nav-link" to="/actions/downloads">{t("downloads")}</NavLink>
                    </li>
                  </Accordion.Body>
                </Accordion.Item>

                <Accordion.Item eventKey="1">
                  <Accordion.Header>
                    <AiOutlineBulb size={20} />
                    {t("inferenceLearning")}
                  </Accordion.Header>
                  <Accordion.Body>
                    <li className="nav-item">
                      <NavLink end className="nav-link" to="/inference">{t("savedLessonsTitle") || "Ver Listado"}</NavLink>
                    </li>
                    <li className="nav-item">
                      <NavLink className="nav-link" to="/inference/create">{t("generateInference") || "Generar Nuevo"}</NavLink>
                    </li>
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>
            </>
          ) : role === "representante" ? (
            <>
            <li className="nav-item">
              <NavLink className="nav-link" to="/representative">
                <IoIosPeople size={20} />
                {t("myStudents")}
              </NavLink>
            </li>
            <li className="nav-item">
              <NavLink className="nav-link" to="/activity">
                <AiOutlineLineChart size={20} />
                {t("clsTitle")}
              </NavLink>
            </li>
            </>
          ) : (
          <>
          <li className="nav-item">
            <Link className="nav-link" to="/dashboard">
              <AiFillDashboard size={20} />
              {t("dashboard")}
            </Link>
          </li>
          {MONETIZATION_ENABLED && (
            <li className="nav-item">
              <Link className="nav-link" to="/subscription">
                <AiOutlineRobot size={20} />
                {t("aiSubscription")}
              </Link>
            </li>
          )}
          <Accordion className="nav-accordion">
            <Accordion.Item eventKey="0">
              <Accordion.Header>
                <BiSpreadsheet size={20} />
                {t("words")}
              </Accordion.Header>
              <Accordion.Body>
                <li className="nav-item">
                  <Link className="nav-link" to="/words/created">
                    {t("added")}
                  </Link>
                </li>
                <li className="nav-item">
                  <Link className="nav-link" to="/words/received">
                    {t("received")}
                  </Link>
                </li>
                <li className="nav-item">
                  <Link className="nav-link" to="/words/sent">
                    {t("sent")}
                  </Link>
                </li>
              </Accordion.Body>
            </Accordion.Item>

            <Accordion.Item eventKey="1">
              <Accordion.Header>
                <IoIosPeople size={20} />
                {t("family")}
              </Accordion.Header>
              <Accordion.Body>
                <li className="nav-item">
                  <Link className="nav-link" to="/family/created">
                    {t("addedMembers")}
                  </Link>
                </li>
              </Accordion.Body>
            </Accordion.Item>

            <Accordion.Item eventKey="2">
              <Accordion.Header>
                <AiOutlineThunderbolt size={20} />
                {t("actions")}
              </Accordion.Header>
              <Accordion.Body>
                <li className="nav-item">
                  <Link className="nav-link" to="/actions/create-text">
                    {t("createText")}
                  </Link>
                </li>
                <li className="nav-item">
                  <Link className="nav-link" to="/actions/list-texts">
                    {t("textList")}
                  </Link>
                </li>
                <li className="nav-item">
                  <Link className="nav-link" to="/actions/downloads">
                    {t("downloads")}
                  </Link>
                </li>
              </Accordion.Body>
            </Accordion.Item>

            <Accordion.Item eventKey="3">
              <Accordion.Header>
                <AiOutlineBulb size={20} />
                {t("inferenceLearning")}
              </Accordion.Header>
              <Accordion.Body>
                <li className="nav-item">
                  <Link className="nav-link" to="/inference">
                    {t("savedLessonsTitle") || "Ver Listado"}
                  </Link>
                </li>
                <li className="nav-item">
                  <Link className="nav-link" to="/inference/create">
                    {t("generateInference") || "Generar Nuevo"}
                  </Link>
                </li>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
          </>
          )}
        </ul>

        <div className="bottom-div-modern">
           <div className="user-info">
            <p className="small mb-1 opacity-50">{t("sessionStarted")}</p>
            <p className="user-email-text mb-3">{user.email}</p>
            <Link className="btn-guide-sidebar w-100 mb-2" to="/user-guide">
              <BiBookOpen size={18} />
              {t("userGuide")}
            </Link>
            <Link
              className="btn-manual-modern w-100"
              to={getManual()}
              download={getManualName()}
              target="_blank"
              rel="noreferrer"
            >
              <AiOutlineDownload size={20} />
              {t("userManual")}
            </Link>

            {/* Idioma y cerrar sesión. En escritorio viven en el header, pero allí
                están ocultos con d-none d-md-flex: sin esto, desde el móvil no hay
                ninguna forma de cerrar sesión ni de cambiar el idioma. */}
            <div className="sidebar-mobile-actions d-md-none">
              <LanguageSelector />
              <button
                type="button"
                className="btn-logout-modern w-100 justify-content-center"
                onClick={handleLogout}
              >
                <RxExit size={18} />
                {t("logout")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </nav>
    </>
  )
}

export default Menu
